import {
  Collector,
  CollectorSet,
  CustomField,
  defaultResponseHandler,
  formatMs,
  MonitorProfile,
  useAssetParams,
  useAssetTranslation,
  useUserinfo,
} from '@mss/mss-component-library';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Fetch from '../../common/util/_fetch';
import { collectorFactory } from '../util/collectorHelpers';
import { AllMonitorsAPI, License } from '../../index';
import { EnrichedAssetMonitor } from '../../common/AssetPages/Edit/MonitorSettings';
import { Modal } from 'antd';
import { useHistory, useRouteMatch } from 'react-router-dom';

export type CustomFieldModels =
  | 'AnalyzerManagementApplication'
  | 'ComputeAsset'
  | 'FieldAsset'
  | 'ControlApplication'
  | 'DCSPLCAsset'
  | 'NetworkAsset'
  | 'EnvAsset'
  | 'AssetManagementApplication'
  | 'ApplicationAsset'
  | 'VirtualizationApplication';

export const useCustomFieldsQuery = (model: CustomFieldModels) => {
  const { t } = useTranslation();
  return useQuery({
    queryKey: ['customFields', model],
    initialData: [],
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
    refetchInterval: formatMs({ hours: 1 }),
    queryFn: async () =>
      (
        await defaultResponseHandler<CustomField[]>({
          request: Fetch.get(`/api/custom-field/?model=${model}`),
          t,
        })
      ).data ?? [],
  });
};

export type CollectorSetAPI = {
  [label: string]: CollectorSet;
};

export type EnrichedCollector = Collector & {
  intervals: CollectorSet['intervals'];
  extra_options?: Record<string, any>;
};
export type CollectorSets = { [label: string]: EnrichedCollector[] };
type CollectorHook = {
  baseCollectors: CollectorSets;
  collectorSets: { key: string; label: string }[];
  raw: CollectorSetAPI;
};
export const useCollectorsQuery = ({
  presetUrl,
  enabledUrl,
  disabled,
}: {
  presetUrl: string;
  enabledUrl?: string;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const collectorsPreset = useQuery({
    enabled: !disabled,
    queryKey: [presetUrl, enabledUrl],
    staleTime: formatMs({ hours: 1 }),
    cacheTime: formatMs({ hours: 1 }),
    initialData: {
      baseCollectors: {},
      collectorSets: [],
      raw: {} as CollectorSetAPI,
    } as CollectorHook,
    queryFn: async () => {
      const { data, error } = await defaultResponseHandler<CollectorSetAPI>({
        t,
        request: Fetch.get(presetUrl),
      });
      if (error)
        return {
          baseCollectors: {},
          collectorSets: [],
          raw: {} as CollectorSetAPI,
        } as CollectorHook;
      const sets = Object.keys(data);
      const baseCollectors = sets.reduce((acc, set) => {
        acc[set] = data[set].collectors.map((x: any) => ({
          ...collectorFactory(
            x.label,
            x.key,
            set,
            data[set].intervals?.default ?? 1440
          ),
          intervals: data[set].intervals,
        }));
        return acc;
      }, {} as CollectorSets);

      return {
        baseCollectors,
        collectorSets: sets.map(set => ({ key: set, label: data[set].label })),
        raw: data,
      };
    },
  });

  const enabledCollectors = useQuery({
    enabled: !!enabledUrl && collectorsPreset.data.collectorSets.length > 0,
    queryKey: [enabledUrl ?? null],
    initialData: collectorsPreset.data.baseCollectors,
    queryFn: async () => {
      const { data, error } = await defaultResponseHandler<EnrichedCollector[]>(
        {
          t,
          request: Fetch.get(enabledUrl ?? ''),
        }
      );
      if (error) return collectorsPreset.data.baseCollectors;
      const newCollectors = { ...collectorsPreset.data.baseCollectors };
      data.forEach((newCollector: EnrichedCollector) => {
        newCollectors[newCollector.collector_type] = newCollectors[
          newCollector.collector_type
        ].map((existing: EnrichedCollector) => {
          if (existing.collector_key === newCollector.collector_key) {
            return {
              ...existing,
              ...newCollector,
            };
          } else {
            return existing;
          }
        });
      });

      return newCollectors;
    },
  });

  const updateCollector = useMutation({
    mutationFn: async (vars: {
      assetId: string;
      collector: EnrichedCollector;
    }): Promise<EnrichedCollector> => {
      const { assetId, collector } = vars;
      const body = {
        collector_type: collector.collector_type,
        collector_key: collector.collector_key,
        asset: assetId,
        enabled: collector.enabled,
        interval: collector.interval || collector.intervals.default,
        extra_options: collector.extra_options,
      };
      const result = await Fetch.put('/api/assets/collector/', {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      return await result.json();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [enabledUrl] });
    },
  });

  return {
    raw: { ...collectorsPreset, data: collectorsPreset.data.raw },
    sets: { ...collectorsPreset, data: collectorsPreset.data.collectorSets },
    collectors:
      Object.keys(enabledCollectors.data).length < 1
        ? { ...enabledCollectors, data: collectorsPreset.data.baseCollectors }
        : enabledCollectors,
    update: updateCollector.mutate,
    isUpdating: updateCollector.isLoading,
  };
};

export const useLicensesQuery = () => {
  const { t } = useTranslation('t');
  return useQuery({
    queryKey: ['license'],
    cacheTime: formatMs({ hours: 6 }),
    staleTime: formatMs({ minutes: 15 }),
    refetchInterval: formatMs({ minutes: 15 }),
    queryFn: async () =>
      (
        await defaultResponseHandler<License>({
          request: Fetch.get('/api/license/'),
          t,
        })
      ).data ?? null,
  });
};

export function useAllMonitorsQuery() {
  const [hasPermission] = useUserinfo();

  const canSetMonitors = hasPermission([
    'change_automationmonitorasset',
    'view_automationmonitorasset',
    'view_automationmonitorprofile',
  ]);

  const { t } = useTranslation('common');
  return useQuery({
    queryKey: ['monitors'],
    cacheTime: formatMs({ hours: 12 }),
    staleTime: formatMs({ hours: 1 }),
    refetchOnWindowFocus: false,
    enabled: canSetMonitors,
    initialData: canSetMonitors ? null : [],
    initialDataUpdatedAt: 1,
    queryFn: async () => {
      const { data } = await defaultResponseHandler<AllMonitorsAPI[]>({
        request: Fetch.get('/api/automation/monitor/'),
        t,
      });

      return data.map((x: AllMonitorsAPI) => ({
        ...x,
        config_json_schema: Object.keys(x.config_json_schema).length
          ? { ...x.config_json_schema }
          : null,
        category: x.category
          .replace('application.', 'security.')
          .replace('.epo', '.trellix_epo')
          .replace('compute.', 'compute.windows_')
          .replace('plc', 'plcdcs')
          .replace('prm', 'assetmanagement.prm')
          .replace('isae', 'assetmanagement.isae')
          .replace('aaims', 'analyzermanagement.aaims')
          .replace('calsys', 'analyzermanagement.calsys'),
      })) as AllMonitorsAPI[];
    },
  });
}

export function useMonitorProfilesQuery() {
  const { assetType = 'field', assetSubType } = useAssetParams();
  const { t } = useAssetTranslation();
  const [hasPermission] = useUserinfo();
  return useQuery({
    queryKey: [
      'monitor-profiles',
      ...[assetType, assetSubType].filter(Boolean),
    ],
    enabled: hasPermission('view_automationmonitorprofile'),
    queryFn: async () =>
      (
        await defaultResponseHandler<MonitorProfile[]>({
          request: Fetch.get(`/api/automation/monitor-profile/`),
          t,
        })
      ).data?.filter(x => {
        const [type, subtype] = x.asset_type.split('.');
        if (!subtype || !assetSubType) return type === assetType;
        return type === assetType && subtype === assetSubType;
      }) ?? [],
  });
}

type ErrorResponse = {
  message: string;
  collectors: { collectors: string[]; type: string };
};

type CustomError = Error & {
  response: ErrorResponse;
  status: number;
  statusText: string;
};

const parseResponse = (response: Response): Promise<ErrorResponse | string> => {
  const contentType = response.headers.get('content-type') || '';

  return contentType.includes('application/json')
    ? response.json().catch(() => response.text())
    : response.text();
};

const createError = (
  response: Response,
  data: ErrorResponse | string
): CustomError =>
  Object.assign(
    new Error(
      typeof data === 'object' && data?.message
        ? data.message
        : `HTTP Error ${response.status}`
    ),
    {
      response: data,
      status: response.status,
      statusText: response.statusText,
    }
  ) as CustomError;

const handleResponse = async <T = any,>(response: Response): Promise<T> => {
  const data = await parseResponse(response);
  if (!response.ok) throw createError(response, data);
  return data as T;
};

export function useMonitorMutation(
  type: 'asset' | 'profile',
  id: string | undefined,
  queryKey: (string | undefined)[],
  setLoadingRows?: (cb: (val: string[]) => string[]) => void,
  collectorAssetUrl?: string
) {
  const queryClient = useQueryClient();
  const baseUrl = `/api/automation/monitor-${type === 'profile' ? 'profile-monitor' : 'asset'}`;
  const { url } = useRouteMatch();
  const history = useHistory();
  const { t } = useAssetTranslation();
  return useMutation({
    mutationFn: async (monitor: EnrichedAssetMonitor) => {
      const monitorExists = !!monitor.id;
      if (monitorExists) {
        const result = await Fetch.patch(`${baseUrl}/${monitor.id}/`, {
          body: JSON.stringify({
            enabled: monitor.enabled,
            config: monitor.config,
            monitor_name: monitor.name,
          }),
        });

        return await handleResponse<EnrichedAssetMonitor>(result);
      } else {
        const result = await Fetch.post(`${baseUrl}/`, {
          body: JSON.stringify({
            [type]: id,
            monitor_name: monitor.name,
            config: monitor.config,
          }),
        });

        return await handleResponse<EnrichedAssetMonitor>(result);
      }
    },
    onMutate: monitor => {
      setLoadingRows?.(prev => [...prev, monitor.name]);
    },
    onError: (error: CustomError, monitor) => {
      setLoadingRows?.(prev => prev.filter(x => x !== monitor.name));
      if (error?.response?.message === 'Missing enabled collector.') {
        const { collectors, type } = error.response.collectors;
        const hasMultipleCollectors = collectors.length > 1;
        const newUrl =
          collectorAssetUrl ?? url.replace('monitor-settings', 'collection-settings');

        const formatCollector = (collector: string) =>
          type ? `${type} - ${collector}` : collector;

        const isPrmFieldAsset = !!collectorAssetUrl;

        const descriptionKey = isPrmFieldAsset
          ? hasMultipleCollectors
            ? 'One of the following collectors on the linked PRM asset needs to be enabled before this monitor can be used'
            : 'The following collector on the linked PRM asset needs to be enabled before this monitor can be used'
          : hasMultipleCollectors
          ? 'One of the following collector needs to be enabled before this monitor can be used'
          : 'The following collector needs to be enabled before this monitor can be used';

        const linkLabel = isPrmFieldAsset
          ? t('Go to PRM collection settings')
          : t('Go to collection settings');

        const modal = Modal.info({
          title: t(`Collector${hasMultipleCollectors ? 's' : ''} Not Enabled`),
          content: (
            <div>
              <p>
                {t(descriptionKey)}
                {hasMultipleCollectors ? ':' : `: `}
                {!hasMultipleCollectors && (
                  <strong>{formatCollector(collectors[0])}</strong>
                )}
              </p>
              {hasMultipleCollectors && (
                <ul>
                  {collectors.map(collector => (
                    <li key={collector}>
                      <strong>{formatCollector(collector)}</strong>
                    </li>
                  ))}
                </ul>
              )}
              <p>
                <a
                  href={newUrl}
                  onClick={e => {
                    e.preventDefault();
                    history.push(newUrl);
                    modal.destroy();
                  }}
                >
                  {linkLabel}
                </a>
              </p>
            </div>
          ),
        });
      }
    },
    onSuccess: (data, monitor) => {
      setLoadingRows?.(prev => prev.filter(x => x !== monitor.name));
      queryClient.setQueryData<EnrichedAssetMonitor[]>(queryKey, oldData =>
        (oldData ?? []).map(x =>
          x.name === monitor.name ? { ...x, ...data } : x
        )
      );
      queryClient.invalidateQueries(['monitor-profiles']);
    },
  });
}
